import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  getDisplayableMenuItems,
  groupItemsBySection,
  sortSectionKeys,
  getSectionDisplayName,
  MenuItemCard,
  MenuItemName,
  MenuItemDescription,
  MenuContainer,
  MenuTitle,
  SectionHeader,
} from "../shared/MenuDisplay";
import RestaurantDetailsLayout from "../shared/RestaurantDetailsLayout";
import FailureReportModal from "./restaurantBuilder/restaurantBuilder2/FailureReportModal";
import {
  logRestaurantWebsiteClick,
  logRestaurantDirectionsClick,
} from "../../util/analytics";

const SourcesSection = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  font-size: 13px;
  color: #757575;
`;

const SourceLink = styled.a`
  color: #1976d2;
  text-decoration: none;
  margin-right: 8px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding: 16px 0 24px;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;
`;

const AddButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #2e7d32;
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #1b5e20;
    color: white;
  }
`;

const ReportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const AdHocBadgeWrapper = styled.span`
  position: relative;
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
`;

const AdHocBadge = styled.span`
  font-size: 12px;
  background-color: #fff3e0;
  color: #e65100;
  padding: 2px 8px;
  border-radius: 12px;
  cursor: help;
`;

const BadgeTooltip = styled.span`
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  margin-bottom: 6px;
  z-index: 10;

  ${AdHocBadgeWrapper}:hover & {
    visibility: visible;
  }
`;

const SourceFootnote = styled.a`
  font-size: 11px;
  color: #9e9e9e;
  margin-left: 4px;
  font-weight: normal;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #1976d2;
  }
`;

const AdHocRestaurantDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const restaurant = location.state?.restaurant;
  const [showReportModal, setShowReportModal] = useState(false);

  if (!restaurant) {
    navigate("/");
    return null;
  }

  // Use allMenuItems (has source_url) for vegan items, fall back to veganOptions
  const veganItems = restaurant.allMenuItems?.length > 0
    ? restaurant.allMenuItems.filter(i => i.category === "vegan")
    : (restaurant.veganOptions || []);
  const menuItems = veganItems.length > 0 ? veganItems : getDisplayableMenuItems(restaurant);

  // Build URL index map for source footnotes
  const contributingUrls = restaurant.contributingUrls || [];
  const urlIndexMap = {};
  contributingUrls.forEach((url, i) => { urlIndexMap[url] = i + 1; });

  // Custom menu rendering with source footnotes on each item
  const menuWithFootnotes = menuItems.length > 0 ? (
    <MenuContainer>
      <MenuTitle>Vegan Options</MenuTitle>
      {(() => {
        const { grouped, sectionOrderMap } = groupItemsBySection(menuItems);
        const sectionKeys = sortSectionKeys(Object.keys(grouped), sectionOrderMap);
        return sectionKeys.map((section) => {
          const sectionItems = grouped[section];
          const displayName = getSectionDisplayName(section);
          const isOtherItems = !section || section === "null";
          return (
            <div key={section || "other"}>
              {(sectionKeys.length > 1 || !isOtherItems) && (
                <SectionHeader>{displayName}</SectionHeader>
              )}
              {sectionItems.map((item, index) => (
                <MenuItemCard key={item._id || index}>
                  <MenuItemName>
                    {item.option}
                    {item.source_url && urlIndexMap[item.source_url] && (
                      <SourceFootnote
                        href={item.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={item.source_url}
                      >
                        [{urlIndexMap[item.source_url]}]
                      </SourceFootnote>
                    )}
                  </MenuItemName>
                  {item.description && (
                    <MenuItemDescription>{item.description}</MenuItemDescription>
                  )}
                </MenuItemCard>
              ))}
            </div>
          );
        });
      })()}
    </MenuContainer>
  ) : (
    <p style={{ color: "#757575", fontStyle: "italic" }}>
      No vegan options were found for this restaurant.
    </p>
  );

  return (
    <RestaurantDetailsLayout
        restaurant={restaurant}
        onDirectionsClick={() => logRestaurantDirectionsClick(restaurant._id, null, "discovery")}
        onWebsiteClick={() => logRestaurantWebsiteClick(restaurant._id, null, "discovery")}
        headerExtra={
          <>
            <AdHocBadgeWrapper>
              <AdHocBadge>Discovery Result</AdHocBadge>
              <BadgeTooltip>
                Processed automatically — results may have errors. Review and edit before adding.
              </BadgeTooltip>
            </AdHocBadgeWrapper>
            <ReportButton onClick={() => setShowReportModal(true)} style={{ marginLeft: "12px" }}>
              <i className="fas fa-flag" />
              Report Issue
            </ReportButton>
          </>
        }
        menuContent={menuWithFootnotes}
        afterMenu={
          contributingUrls.length > 0 ? (
            <SourcesSection>
              <b>Sources: </b>
              {contributingUrls.map((url, i) => (
                <SourceLink
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  [{i + 1}]
                </SourceLink>
              ))}
            </SourcesSection>
          ) : null
        }
      >
        <ActionBar>
          <AddButton
            to="/create/v2/cuisine"
            state={{ restaurant, fromAdHoc: true }}
          >
            <i className="fas fa-edit" />
            Edit & Add Restaurant
          </AddButton>
        </ActionBar>

        <FailureReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          restaurantName={restaurant.name}
          restaurantWebsite={restaurant.website}
          hideMenuUrl
        />
      </RestaurantDetailsLayout>
  );
};

export default AdHocRestaurantDetails;
